import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatCurrency, formatDate, getReceiptUrl, CATEGORIES_EXPENSE, CATEGORIES_INCOME, compressImage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useEntity } from "../lib/entity";
import Layout from "../components/Layout";
import HelpTip from "../components/Help/HelpTip";
import EmptyState from "../components/Help/EmptyState";
import TransactionDetailModal from "../components/TransactionDetailModal";
import CategoryGuidanceCard from "../components/CategoryGuidanceCard";
import SmartClarifyingPrompt from "../components/SmartClarifyingPrompt";
import MortgageBreakdownCard from "../components/MortgageBreakdownCard";
import MissingReceiptModal from "../components/MissingReceiptModal";
import DeductibilityStatusPill from "../components/DeductibilityStatusPill";
import DocumentAnalysisProgress, { useDocumentAnalysisProgress } from "../components/DocumentAnalysisProgress";
import BulkReceiptCard from "../components/BulkReceiptCard";
import { HELP_TOPICS } from "../constants/helpContent";
import { getCategoryConfig } from "../constants/categoryConfig";
import { TransactionsSkeleton, DataErrorState } from "../components/Skeleton/SkeletonComponents";

import {
  Plus, ArrowUpRight, ArrowDownRight, Trash2, ScanLine, FileText,
  Paperclip, Sparkles, Loader2, CheckCircle2, ListChecks, Check, X,
  Upload, Receipt, ShieldCheck, Flag, Building, ArrowRight, DollarSign, PieChart,
  ExternalLink, Layers, Files, Search
} from "lucide-react";
import { toast } from "sonner";
import { Field, SelectField } from "./Properties";

const CATEGORY_TOPIC_MAP = {
  "Advertising": "cat-advertising",
  "Auto & Travel": "cat-auto-travel",
  "Cleaning & Maintenance": "cat-cleaning-maintenance",
  "Commissions": "cat-commissions",
  "Insurance": "cat-insurance",
  "Legal & Professional": "cat-legal-professional",
  "Management Fees": "cat-management-fees",
  "Meals & Entertainment": "cat-auto-travel",
  "Mortgage Interest": "cat-mortgage-interest",
  "Repairs": "cat-repairs",
  "Supplies": "cat-supplies",
  "Taxes": "cat-taxes",
  "Utilities": "cat-utilities",
  "Depreciation": "cat-depreciation",
  "Capital Contribution": "capital-contribution",
  "Owner Draw": "owner-draw",
  "Due to Member": "due-to-member",
  "Rent": "income-types",
  "STR Booking": "leasing-type",
};


const EMPTY = {
  date: new Date().toISOString().slice(0, 10),
  amount: "",
  type: "Expense",
  category: "Repairs",
  property_id: "",
  vendor_id: "",
  tenant_id: "",
  notes: "",
  receipt_path: "",
  receipt_items: "",
  deductible_pct: 100,
  deductible_amount: "",
  principal_amount: 0,
  interest_amount: 0,
  escrow_amount: 0,
  deductibility_reason: "100% routine maintenance and repairs",
  needs_cpa_review: false,
  cpa_review_reason: "",
  is_capital_improvement: false,
  cpa_resolved: false,
  cpa_notes: "",
};

function SwipeableTransactionRow({ tx, onSelect, onDelete, canDelete, onSwipeAction }) {
  const [offsetX, setOffsetX] = useState(0);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isHorizontalSwipe = useRef(null);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isHorizontalSwipe.current = null;
  };

  const handleTouchMove = (e) => {
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const diffX = currentX - touchStartX.current;
    const diffY = currentY - touchStartY.current;

    if (isHorizontalSwipe.current === null) {
      if (Math.abs(diffX) > 8 || Math.abs(diffY) > 8) {
        isHorizontalSwipe.current = Math.abs(diffX) > Math.abs(diffY);
      }
    }

    if (isHorizontalSwipe.current) {
      if (diffX < 0) {
        setOffsetX(Math.max(-80, diffX));
      } else if (offsetX < 0) {
        setOffsetX(Math.min(0, offsetX + diffX));
      }
    }
  };

  const handleTouchEnd = () => {
    if (isHorizontalSwipe.current && offsetX < -35) {
      setOffsetX(-80);
      if (onSwipeAction) onSwipeAction();
    } else {
      setOffsetX(0);
    }
  };

  const iconConfig = getCategoryConfig(tx.category, tx.type);
  const IconComp = iconConfig.icon;

  const isExpense = tx.type === "Expense";
  const pct = tx.deductible_pct != null ? Number(tx.deductible_pct) : 100;
  const hasCustomDeductible =
    tx.deductible_amount != null &&
    tx.deductible_amount !== "" &&
    Number(tx.deductible_amount) < Number(tx.amount || 0);
  const isUnder100 = pct < 99.9 || hasCustomDeductible;
  const showDeductibleLine = isExpense && isUnder100;
  const deductibleAmount = hasCustomDeductible
    ? Number(tx.deductible_amount)
    : Number(tx.amount || 0) * (pct / 100);

  return (
    <div className="relative overflow-hidden group/row border-b border-[var(--border)] last:border-b-0">
      {/* Revealed Swipe-to-Delete Action on Mobile (Underneath) */}
      {canDelete && (
        <div className="sm:hidden absolute right-0 top-0 bottom-0 w-20 bg-red-600 flex items-center justify-center z-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(tx.id);
            }}
            className="w-full h-full flex flex-col items-center justify-center text-white text-xs font-bold gap-1 active:bg-red-700 cursor-pointer"
            data-testid={`tx-swipe-delete-${tx.id}`}
          >
            <Trash2 size={18} />
            <span>Delete</span>
          </button>
        </div>
      )}

      {/* Foreground Interactive Card Row */}
      <div
        onClick={() => {
          if (offsetX !== 0) {
            setOffsetX(0);
          } else {
            onSelect(tx);
          }
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: offsetX !== 0 ? `translateX(${offsetX}px)` : undefined,
          transition: isHorizontalSwipe.current ? "none" : "transform 0.2s ease-out",
        }}
        className="relative z-10 bg-[var(--surface)] hover:bg-[var(--surface-hover)] min-h-[72px] py-3.5 px-4 flex items-center w-full gap-3 transition-colors cursor-pointer group select-none"
        data-testid={`tx-row-${tx.id}`}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect(tx);
          }
        }}
      >
        {/* Left Side: Category Icon Circle (40px, never shrinks) */}
        <div
          style={{ backgroundColor: iconConfig.color }}
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-white shadow-sm"
        >
          <IconComp size={18} className="text-white" />
        </div>

        {/* Middle: Text Column (flex 1, min-width 0, overflow hidden) */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <span className="font-semibold text-[15px] text-[var(--text)] group-hover:text-teal-400 transition-colors whitespace-nowrap overflow-hidden text-ellipsis max-w-full block">
            {tx.category}
          </span>
          <div className="text-xs text-[var(--muted)] whitespace-nowrap overflow-hidden text-ellipsis block mt-0.5 font-medium">
            {formatDate(tx.date)} · {[tx.property_name, tx.vendor_name || tx.tenant_name].filter(Boolean).join(" · ") || "Unassigned"}
            {tx.notes ? ` · ${tx.notes}` : ""}
          </div>
        </div>

        {/* Badge Column (Separate flex item, max-w-[140px], shrink-0) */}
        <div className="flex items-center gap-1 shrink-0 max-w-[140px]">
          {tx.type === "Expense" && (
            <DeductibilityStatusPill
              deductiblePct={tx.deductible_pct}
              isCapitalImprovement={tx.is_capital_improvement}
              needsCpaReview={tx.needs_cpa_review}
              cpaResolved={tx.cpa_resolved}
              className="shrink min-w-0"
            />
          )}
          {tx.receipt_path && (
            <span
              title="Receipt Attached"
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/15 text-emerald-400 shrink whitespace-nowrap min-w-0"
            >
              <Paperclip size={11} className="shrink-0" />
              <span className="hidden min-[360px]:inline">Receipt</span>
            </span>
          )}
        </div>

        {/* Right: Amount Column (shrink-0, min-w-[80px], max-w-[100px]) */}
        <div className="shrink-0 text-right min-w-[80px] max-w-[100px] flex flex-col justify-center items-end">
          <div
            className={`font-bold tabular-nums tracking-tight text-[16px] whitespace-nowrap ${
              tx.type === "Income" ? "text-emerald-400" : "text-rose-400"
            }`}
          >
            {tx.type === "Income" ? "+" : "−"}{formatCurrency(tx.amount)}
          </div>
          {showDeductibleLine && (
            <div className="text-[11px] text-[var(--muted)] font-medium mt-0.5 whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
              Ded: {formatCurrency(deductibleAmount)}
            </div>
          )}
        </div>

        {/* Desktop Delete Trash Icon */}
        {canDelete && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(tx.id);
            }}
            data-testid={`tx-delete-${tx.id}`}
            className="hidden sm:flex w-10 h-10 rounded-xl text-slate-400 hover:text-red-400 hover:bg-red-500/10 items-center justify-center transition-colors ml-2 shrink-0"
            title="Delete transaction"
            aria-label="Delete transaction"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function Transactions() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);

  const [txs, setTxs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [taxParameters, setTaxParameters] = useState({
    irs_mileage_rate: 0.70,
    business_meals_deductible_pct: 50,
    bonus_depreciation_pct: 40,
    tax_year: 2025,
    show_smart_deductibility_prompts: true,
    receipt_warning_threshold: 75,
  });
  const [showMissingReceiptModal, setShowMissingReceiptModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState("All");
  const [selectedTx, setSelectedTx] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadProgressText, setUploadProgressText] = useState("");

  const [attachmentFile, setAttachmentFile] = useState(null);
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const aiFileInputRef = useRef(null);
  const bulkFileInputRef = useRef(null);
  const formRef = useRef(null);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [bulkReceipts, setBulkReceipts] = useState([]);
  const [savingBulk, setSavingBulk] = useState(false);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { can = {} } = useAuth() || {};
  const { activeEntityId = "all" } = useEntity() || {};

  // Mobile Polish States (Bug 8.B & 8.C)
  const [hasSeenSwipeHint, setHasSeenSwipeHint] = useState(() => {
    try {
      return localStorage.getItem("has_seen_swipe_hint") === "true";
    } catch {
      return false;
    }
  });
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const isPullingRef = useRef(false);

  const dismissSwipeHint = () => {
    try {
      localStorage.setItem("has_seen_swipe_hint", "true");
    } catch {}
    setHasSeenSwipeHint(true);
  };

  const handleTouchStartList = (e) => {
    if (window.scrollY <= 5 && !isRefreshing) {
      touchStartY.current = e.touches[0].clientY;
      isPullingRef.current = true;
    } else {
      isPullingRef.current = false;
    }
  };

  const handleTouchMoveList = (e) => {
    if (!isPullingRef.current || isRefreshing) return;
    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartY.current;
    if (diff > 0 && window.scrollY <= 5) {
      const damped = Math.min(75, diff * 0.45);
      setPullDistance(damped);
    } else {
      setPullDistance(0);
    }
  };

  const handleTouchEndList = async () => {
    if (!isPullingRef.current) return;
    isPullingRef.current = false;
    if (pullDistance >= 45 && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(45);
      try {
        await load();
        toast.success("Transactions refreshed");
      } catch (err) {
        console.error(err);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  };

  // AI OCR States for New Expense
  const [scanWithAi, setScanWithAi] = useState(false);
  const [scanningAi, setScanningAi] = useState(false);
  const {
    progress: aiScanProgress,
    status: aiScanStatus,
    stepText: aiScanStepText,
    docName: aiScanDocName,
    startProgress: startAiScanProgress,
    completeProgress: completeAiScanProgress,
    resetProgress: resetAiScanProgress,
  } = useDocumentAnalysisProgress();
  const [aiItems, setAiItems] = useState([]);
  const [isItemized, setIsItemized] = useState(false);
  const [originalAiTotal, setOriginalAiTotal] = useState("");
  const [detectedVendor, setDetectedVendor] = useState("");
  const [showQuickAddVendor, setShowQuickAddVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState("");
  const [creatingVendor, setCreatingVendor] = useState(false);

  // Quick add vendor handler
  const handleQuickAddVendor = async (nameToCreate) => {
    const name = (nameToCreate || newVendorName).trim();
    if (!name) return;
    setCreatingVendor(true);
    try {
      const res = await api.post("/vendors", {
        name,
        category: form.category || "Repairs",
      });
      const newV = res.data;
      setVendors((prev) => [...prev, newV]);
      setForm((prev) => ({ ...prev, vendor_id: newV.id }));
      setShowQuickAddVendor(false);
      setNewVendorName("");
      toast.success(`Vendor "${name}" created and assigned!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create vendor");
    } finally {
      setCreatingVendor(false);
    }
  };

  const load = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    setLoadError(null);
    const startTime = Date.now();
    try {
      const q = activeEntityId !== "all" ? { entity_id: activeEntityId } : {};
      const [tRes, pRes, vRes, tnRes, tpRes] = await Promise.all([
        api.get("/transactions", { params: q }),
        api.get("/properties", { params: q }),
        api.get("/vendors"),
        api.get("/tenants"),
        api.get("/tax-parameters").catch(() => ({ data: null })),
      ]);
      setTxs(Array.isArray(tRes?.data) ? tRes.data : []);
      setProperties(Array.isArray(pRes?.data) ? pRes.data : []);
      setVendors(Array.isArray(vRes?.data) ? vRes.data : []);
      setTenants(Array.isArray(tnRes?.data) ? tnRes.data : []);
      if (tpRes?.data) setTaxParameters(tpRes.data);
    } catch (err) {
      console.error("Failed to load transactions:", err);
      setLoadError("Failed to load transactions. Tap below to retry.");
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 300 - elapsed);
      setTimeout(() => {
        setLoading(false);
      }, remaining);
    }
  }, [activeEntityId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (params.get("new")) {
      setShowForm(true);
      setParams({});
    }
  }, [params, setParams]);

  // Smoothly scroll to the form when opened
  useEffect(() => {
    if (showForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [showForm]);

  // Tuck away bottom navigation menu while modal dialogs are open
  useEffect(() => {
    if (selectedTx || showMissingReceiptModal) {
      document.body.classList.add("has-modal-open");
    } else {
      document.body.classList.remove("has-modal-open");
    }
    return () => {
      document.body.classList.remove("has-modal-open");
    };
  }, [selectedTx, showMissingReceiptModal]);

  // Standard manual file attachment
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File exceeds 25MB maximum limit. Please compress or optimize the PDF.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setUploadProgressText("0%");
    try {
      const fd = new FormData();
      fd.append("file", file);
      // NOTE: Do not manually set 'Content-Type: multipart/form-data'
      // Axios & the browser automatically add the boundary attribute
      const r = await api.post("/transactions/upload-attachment", fd, {
        timeout: 60000,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            const loadedMb = (progressEvent.loaded / (1024 * 1024)).toFixed(1);
            const totalMb = (progressEvent.total / (1024 * 1024)).toFixed(1);
            setUploadProgress(pct);
            setUploadProgressText(`${pct}% (${loadedMb}MB / ${totalMb}MB)`);
          } else {
            setUploadProgressText("Uploading…");
          }
        },
      });
      setForm((prev) => ({ ...prev, receipt_path: r.data.receipt_path }));
      setAttachmentFile(file);
      try {
        setReceiptPreviewUrl(URL.createObjectURL(file));
      } catch {
        setReceiptPreviewUrl(null);
      }
      toast.success("Receipt attached for IRS audit record");
    } catch (err) {
      let msg = "Attachment upload failed";
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        msg = "Upload timed out after 60s. The network connection to your NAS is slow or the file is too large. Please retry.";
      } else if (err.response?.status === 413) {
        msg = "File exceeds server or reverse-proxy upload limit (max 25MB). Please reduce the PDF size.";
      } else if (err.response?.status === 504) {
        msg = "Gateway timeout (504). The reverse proxy took too long receiving the file. Please retry.";
      } else if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      } else if (!err.response && err.message) {
        msg = `Upload error: ${err.message}`;
      }
      toast.error(msg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setUploadProgressText("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Reusable helper to extract receipt with AI OCR
  const extractReceiptWithAi = async (file) => {
    const uploadFile = await compressImage(file);
    const fd = new FormData();
    fd.append("file", uploadFile);
    // Boundary is automatically generated by browser/Axios when headers is omitted
    const res = await api.post("/ai/extract-receipt", fd, {
      timeout: 90000,
    });
    const data = res.data;

    let previewUrl = null;
    try {
      previewUrl = URL.createObjectURL(uploadFile);
    } catch {
      previewUrl = null;
    }

    // Line items
    const extractedItems = (data.items || []).map((it, idx) => ({
      id: idx + 1,
      description: it.description || `Item ${idx + 1}`,
      amount: it.amount != null ? it.amount.toString() : "0.00",
      selected: true,
    }));

    // Smart vendor matching
    let matchedVendorId = "";
    if (data.vendor) {
      const normD = (data.vendor || "").toLowerCase().replace(/^(the|a|an)\s+/i, "").replace(/[^a-z0-9]/g, "");
      const found = vendors.find((v) => {
        const normV = (v.name || "").toLowerCase().replace(/^(the|a|an)\s+/i, "").replace(/[^a-z0-9]/g, "");
        return normV === normD || normV.includes(normD) || normD.includes(normV);
      });
      if (found) matchedVendorId = found.id;
    }

    // Smart property matching
    let matchedPropId = data.matched_property_id || "";
    if (!matchedPropId && (data.property_address || data.vendor)) {
      const queryText = `${data.property_address || ""} ${data.notes || ""}`.toLowerCase().replace(/[^a-z0-9]/g, "");
      const foundProp = properties.find((p) => {
        const pAddr = (p.address || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        const pName = (p.name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
        return (pAddr && queryText.includes(pAddr)) || (pName && queryText.includes(pName));
      });
      if (foundProp) matchedPropId = foundProp.id;
    }

    const isMortgage =
      (data.suggested_category && data.suggested_category.toLowerCase().includes("mortgage")) ||
      (data.vendor && data.vendor.toLowerCase().includes("mortgage")) ||
      (file?.name && file.name.toLowerCase().includes("mortgage")) ||
      (data.principal_amount != null && data.principal_amount > 0);

    const resolvedCategory = data.suggested_category
      ? data.suggested_category
      : (isMortgage ? "Mortgage Interest" : (form.category || "Repairs"));

    return {
      data,
      uploadFile,
      previewUrl,
      extractedItems,
      matchedVendorId,
      matchedPropId,
      isMortgage,
      resolvedCategory,
    };
  };

  // AI OCR file extraction for New Expense (Single Mode)
  const handleAiScanFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File exceeds 25MB limit");
      return;
    }
    setScanningAi(true);
    startAiScanProgress(file.name);
    try {
      const {
        data,
        uploadFile,
        previewUrl,
        extractedItems,
        matchedVendorId,
        matchedPropId,
        isMortgage,
        resolvedCategory,
      } = await extractReceiptWithAi(file);

      setAttachmentFile(file);
      setReceiptPreviewUrl(previewUrl);
      setAiItems(extractedItems);
      setOriginalAiTotal(data.total_amount != null ? data.total_amount.toString() : "");
      setIsItemized(false);

      if (data.vendor) {
        setDetectedVendor(data.vendor);
        if (matchedVendorId) {
          const vObj = vendors.find((v) => v.id === matchedVendorId);
          if (vObj) toast.info(`Matched merchant to existing vendor: "${vObj.name}"`);
        }
      } else {
        setDetectedVendor("");
      }

      if (matchedPropId) {
        const pObj = properties.find((p) => p.id === matchedPropId);
        if (pObj) toast.info(`Matched to property: "${pObj.name}"`);
      }

      setForm((prev) => ({
        ...prev,
        date: data.date || prev.date,
        amount: data.total_amount != null ? data.total_amount.toString() : prev.amount,
        category: resolvedCategory,
        property_id: matchedPropId || prev.property_id,
        vendor_id: matchedVendorId || prev.vendor_id,
        notes: data.vendor
          ? (isMortgage ? `Mortgage: ${data.vendor} (P&I + Escrow)` : `Receipt: ${data.vendor}`)
          : prev.notes,
        receipt_path: data.receipt_path || "",
        receipt_items: JSON.stringify(extractedItems),
        deductible_pct: data.deductible_pct != null ? data.deductible_pct : (isMortgage ? 91.2 : 100),
        deductible_amount: data.deductible_amount != null ? data.deductible_amount.toString() : prev.deductible_amount,
        principal_amount: data.principal_amount != null ? data.principal_amount : (isMortgage && prev.principal_amount ? prev.principal_amount : 0),
        interest_amount: data.interest_amount != null ? data.interest_amount : (isMortgage && prev.interest_amount ? prev.interest_amount : 0),
        escrow_amount: data.escrow_amount != null ? data.escrow_amount : (isMortgage && prev.escrow_amount ? prev.escrow_amount : 0),
        deductibility_reason: data.deductibility_reason || (isMortgage ? "Principal is non-deductible; Interest + Escrow is Schedule E deductible." : prev.deductibility_reason),
      }));

      completeAiScanProgress();
      await new Promise((resolve) => setTimeout(resolve, 400));

      if (data.total_amount == null && data.raw) {
        toast.warning(data.raw.slice(0, 160));
      } else {
        toast.success(`Statement analyzed & attached! Extracted: $${data.total_amount || 0}`);
      }
    } catch (err) {
      resetAiScanProgress();
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        toast.error("Analysis timed out. Please try again with a clearer image or check Gemini API status.");
      } else {
        toast.error(err.response?.data?.detail || "AI receipt scan failed");
      }
    } finally {
      setScanningAi(false);
      if (aiFileInputRef.current) {
        aiFileInputRef.current.value = "";
      }
    }
  };

  // Bulk File Selection & Processing (Bulk Mode)
  const handleBulkFileSelect = async (e) => {
    const fileList = Array.from(e.target.files || []);
    if (!fileList.length) return;
    if (e.target) e.target.value = "";

    if (scanWithAi) {
      setScanningAi(true);
      const newItems = [...bulkReceipts];
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        if (file.size > 25 * 1024 * 1024) {
          toast.error(`File "${file.name}" exceeds 25MB limit`);
          continue;
        }

        startAiScanProgress(`${file.name} (${i + 1} of ${fileList.length})`);
        try {
          const res = await extractReceiptWithAi(file);
          const {
            data,
            previewUrl,
            extractedItems,
            matchedVendorId,
            matchedPropId,
            isMortgage,
            resolvedCategory,
          } = res;

          newItems.push({
            id: "bulk_" + Math.random().toString(36).slice(2, 10),
            file,
            filename: file.name,
            previewUrl,
            receipt_path: data.receipt_path || "",
            receipt_items: JSON.stringify(extractedItems),
            date: data.date || new Date().toISOString().slice(0, 10),
            amount: data.total_amount != null ? data.total_amount.toString() : "",
            category: resolvedCategory,
            property_id: matchedPropId,
            vendor_id: matchedVendorId,
            notes: data.vendor
              ? (isMortgage ? `Mortgage: ${data.vendor} (P&I + Escrow)` : `Receipt: ${data.vendor}`)
              : "",
            deductible_pct: data.deductible_pct != null ? data.deductible_pct : (isMortgage ? 91.2 : 100),
            deductible_amount: data.deductible_amount != null ? data.deductible_amount.toString() : (data.total_amount ? data.total_amount.toString() : ""),
            principal_amount: data.principal_amount || 0,
            interest_amount: data.interest_amount || 0,
            escrow_amount: data.escrow_amount || 0,
            deductibility_reason: data.deductibility_reason || (isMortgage ? "Principal is non-deductible; Interest + Escrow is Schedule E deductible." : null),
            needs_cpa_review: false,
            is_capital_improvement: false,
            aiAnalyzed: true,
          });
        } catch (err) {
          console.error("AI OCR error for", file.name, err);
          let previewUrl = null;
          try { previewUrl = URL.createObjectURL(file); } catch {}
          newItems.push({
            id: "bulk_" + Math.random().toString(36).slice(2, 10),
            file,
            filename: file.name,
            previewUrl,
            receipt_path: "",
            date: new Date().toISOString().slice(0, 10),
            amount: "",
            category: "Repairs",
            property_id: "",
            vendor_id: "",
            notes: `Receipt: ${file.name}`,
            deductible_pct: 100,
            deductible_amount: "",
            principal_amount: 0,
            interest_amount: 0,
            escrow_amount: 0,
            aiAnalyzed: false,
          });
        }
      }
      setBulkReceipts(newItems);
      completeAiScanProgress();
      setScanningAi(false);
      toast.success(`Processed ${fileList.length} receipts! Each receipt is categorized below.`);
    } else {
      // Manual upload without AI
      setUploading(true);
      const newItems = [...bulkReceipts];
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        try {
          const fd = new FormData();
          fd.append("file", file);
          const res = await api.post("/transactions/upload-attachment", fd, {
            timeout: 60000,
          });

          let previewUrl = null;
          try { previewUrl = URL.createObjectURL(file); } catch {}

          newItems.push({
            id: "bulk_" + Math.random().toString(36).slice(2, 10),
            file,
            filename: file.name,
            previewUrl,
            receipt_path: res.data.receipt_path,
            date: new Date().toISOString().slice(0, 10),
            amount: "",
            category: "Repairs",
            property_id: "",
            vendor_id: "",
            notes: "",
            deductible_pct: 100,
            deductible_amount: "",
            principal_amount: 0,
            interest_amount: 0,
            escrow_amount: 0,
            aiAnalyzed: false,
          });
        } catch (err) {
          toast.error(`Failed to upload ${file.name}`);
        }
      }
      setBulkReceipts(newItems);
      setUploading(false);
      toast.success(`Added ${fileList.length} receipts for categorization.`);
    }
  };

  // Submit all bulk transactions
  const submitBulkTransactions = async (e) => {
    if (e) e.preventDefault();
    if (!bulkReceipts.length) {
      toast.error("Please add at least one receipt.");
      return;
    }

    const invalidIndex = bulkReceipts.findIndex((r) => !r.amount || parseFloat(r.amount) <= 0);
    if (invalidIndex !== -1) {
      toast.error(`Receipt #${invalidIndex + 1} (${bulkReceipts[invalidIndex].filename || "Untitled"}) is missing a valid amount.`);
      return;
    }

    setSavingBulk(true);
    try {
      const payloadTransactions = bulkReceipts.map((r) => {
        const amtVal = parseFloat(r.amount) || 0;
        const dedPctVal = parseFloat(r.deductible_pct) ?? 100;
        const dedAmtVal = r.deductible_amount !== "" && r.deductible_amount != null
          ? parseFloat(r.deductible_amount)
          : Number((amtVal * (dedPctVal / 100)).toFixed(2));

        return {
          date: r.date || new Date().toISOString().slice(0, 10),
          amount: amtVal,
          type: "Expense",
          category: r.category || "Repairs",
          property_id: r.property_id || null,
          vendor_id: r.vendor_id || null,
          tenant_id: null,
          notes: r.notes || null,
          receipt_path: r.receipt_path || null,
          receipt_items: r.receipt_items || null,
          deductible_pct: dedPctVal,
          deductible_amount: dedAmtVal,
          principal_amount: parseFloat(r.principal_amount) || 0,
          interest_amount: parseFloat(r.interest_amount) || 0,
          escrow_amount: parseFloat(r.escrow_amount) || 0,
          deductibility_reason: r.deductibility_reason || null,
          needs_cpa_review: Boolean(r.needs_cpa_review),
          cpa_review_reason: r.needs_cpa_review ? "Flagged during bulk receipt import" : null,
          is_capital_improvement: Boolean(r.is_capital_improvement),
          cpa_resolved: false,
          cpa_notes: null,
        };
      });

      const res = await api.post("/transactions/bulk", { transactions: payloadTransactions });
      toast.success(`🎉 Successfully recorded ${res.data.count || payloadTransactions.length} transactions!`);
      setShowForm(false);
      setBulkReceipts([]);
      setIsBulkMode(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save bulk transactions");
    } finally {
      setSavingBulk(false);
    }
  };

  const handleScanExistingAttachment = async () => {
    if (!attachmentFile) return;
    setScanningAi(true);
    startAiScanProgress(attachmentFile?.name || (form.receipt_path ? form.receipt_path.split("/").pop() : "Attached document"));
    try {
      const uploadFile = await compressImage(attachmentFile);
      const fd = new FormData();
      fd.append("file", uploadFile);
      const res = await api.post("/ai/extract-receipt", fd, {
        timeout: 90000,
      });
      const data = res.data;
      const extractedItems = (data.items || []).map((it, idx) => ({
        id: idx + 1,
        description: it.description || `Item ${idx + 1}`,
        amount: it.amount != null ? it.amount.toString() : "0.00",
        selected: true,
      }));
      setAiItems(extractedItems);
      setOriginalAiTotal(data.total_amount != null ? data.total_amount.toString() : "");
      setIsItemized(false);

      let matchedVendorId = "";
      if (data.vendor) {
        setDetectedVendor(data.vendor);
        const normD = (data.vendor || "").toLowerCase().replace(/^(the|a|an)\s+/i, "").replace(/[^a-z0-9]/g, "");
        const found = vendors.find((v) => {
          const normV = (v.name || "").toLowerCase().replace(/^(the|a|an)\s+/i, "").replace(/[^a-z0-9]/g, "");
          return normV === normD || normV.includes(normD) || normD.includes(normV);
        });
        if (found) {
          matchedVendorId = found.id;
          toast.info(`Matched merchant to existing vendor: "${found.name}"`);
        }
      } else {
        setDetectedVendor("");
      }

      // Smart property matching
      let matchedPropId = data.matched_property_id || "";
      if (!matchedPropId && (data.property_address || data.vendor)) {
        const queryText = `${data.property_address || ""} ${data.notes || ""}`.toLowerCase().replace(/[^a-z0-9]/g, "");
        const foundProp = properties.find((p) => {
          const pAddr = (p.address || "").toLowerCase().replace(/[^a-z0-9]/g, "");
          const pName = (p.name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
          return (pAddr && queryText.includes(pAddr)) || (pName && queryText.includes(pName));
        });
        if (foundProp) {
          matchedPropId = foundProp.id;
        }
      }
      if (matchedPropId) {
        const pObj = properties.find((p) => p.id === matchedPropId);
        if (pObj) {
          toast.info(`Matched to property: "${pObj.name}"`);
        }
      }

      const isMortgage =
        (data.suggested_category && data.suggested_category.toLowerCase().includes("mortgage")) ||
        (data.vendor && data.vendor.toLowerCase().includes("mortgage")) ||
        (attachmentFile?.name && attachmentFile.name.toLowerCase().includes("mortgage")) ||
        (data.principal_amount != null && data.principal_amount > 0);

      const resolvedCategory = data.suggested_category
        ? data.suggested_category
        : (isMortgage ? "Mortgage Interest" : (form.category || "Repairs"));

      setForm((prev) => ({
        ...prev,
        date: data.date || prev.date,
        amount: data.total_amount != null ? data.total_amount.toString() : prev.amount,
        category: resolvedCategory,
        property_id: matchedPropId || prev.property_id,
        vendor_id: matchedVendorId || prev.vendor_id,
        notes: data.vendor
          ? (isMortgage ? `Mortgage: ${data.vendor} (P&I + Escrow)` : `Receipt: ${data.vendor}`)
          : prev.notes,
        receipt_path: data.receipt_path || prev.receipt_path,
        receipt_items: JSON.stringify(extractedItems),
        deductible_pct: data.deductible_pct != null ? data.deductible_pct : (isMortgage ? 91.2 : 100),
        deductible_amount: data.deductible_amount != null ? data.deductible_amount.toString() : prev.deductible_amount,
        principal_amount: data.principal_amount != null ? data.principal_amount : (isMortgage && prev.principal_amount ? prev.principal_amount : 0),
        interest_amount: data.interest_amount != null ? data.interest_amount : (isMortgage && prev.interest_amount ? prev.interest_amount : 0),
        escrow_amount: data.escrow_amount != null ? data.escrow_amount : (isMortgage && prev.escrow_amount ? prev.escrow_amount : 0),
        deductibility_reason: data.deductibility_reason || (isMortgage ? "Principal is non-deductible; Interest + Escrow is Schedule E deductible." : prev.deductibility_reason),
      }));

      completeAiScanProgress();
      await new Promise((resolve) => setTimeout(resolve, 400));

      if (data.total_amount == null && data.raw) {
        toast.warning(data.raw.slice(0, 160));
      } else {
        toast.success(`Statement analyzed! Extracted amount: $${data.total_amount || 0}`);
      }
    } catch (err) {
      resetAiScanProgress();
      toast.error(err.response?.data?.detail || "AI scan failed");
    } finally {
      setScanningAi(false);
    }
  };

  const handleRemoveReceipt = () => {
    setForm((prev) => ({ ...prev, receipt_path: "", receipt_items: null }));
    setAttachmentFile(null);
    resetAiScanProgress();
    if (receiptPreviewUrl) {
      try {
        URL.revokeObjectURL(receiptPreviewUrl);
      } catch {}
      setReceiptPreviewUrl(null);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (aiFileInputRef.current) aiFileInputRef.current.value = "";
    toast.info("Receipt attachment removed");
  };

  // Itemize toggles
  const toggleItemize = (enabled) => {
    setIsItemized(enabled);
    if (enabled) {
      const sumSelected = aiItems
        .filter((i) => i.selected)
        .reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
      setForm((prev) => ({
        ...prev,
        amount: sumSelected > 0 ? sumSelected.toFixed(2) : prev.amount,
        notes: prev.notes ? `${prev.notes} (Itemized)` : "(Itemized)",
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        amount: originalAiTotal || prev.amount,
        notes: prev.notes.replace(/\s*\(Itemized\)/g, "").replace(/\s*\[Excluded.*?\]/g, ""),
      }));
    }
  };

  const toggleItemCheck = (idx, checked) => {
    const updated = aiItems.map((it, i) => (i === idx ? { ...it, selected: checked } : it));
    setAiItems(updated);
    if (isItemized) {
      const sumSelected = updated
        .filter((i) => i.selected)
        .reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
      const excludedSum = updated
        .filter((i) => !i.selected)
        .reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);

      const baseNote = form.notes.replace(/\s*\[Excluded.*?\]/g, "");
      const noteWithExcluded = excludedSum > 0
        ? `${baseNote} [Excluded $${excludedSum.toFixed(2)} personal items]`.trim()
        : baseNote;

      setForm((prev) => ({
        ...prev,
        amount: sumSelected.toFixed(2),
        notes: noteWithExcluded,
        receipt_items: JSON.stringify(updated),
      }));
    }
  };

  const handleMortgageBreakdownChange = (mortgageData) => {
    setForm((prev) => ({
      ...prev,
      amount: mortgageData.amount ? mortgageData.amount.toString() : prev.amount,
      principal_amount: mortgageData.principalAmount,
      interest_amount: mortgageData.interestAmount,
      escrow_amount: mortgageData.escrowAmount,
      deductible_amount: mortgageData.deductibleAmount,
      deductible_pct: mortgageData.deductiblePct,
      deductibility_reason:
        mortgageData.principalAmount > 0
          ? `Mortgage Principal of $${mortgageData.principalAmount.toFixed(2)} is non-deductible equity reduction; $${mortgageData.deductibleAmount.toFixed(2)} (Interest + Escrow) is Schedule E deductible.`
          : prev.deductibility_reason,
    }));
  };

  const executeSave = async (overridePayload = null) => {
    try {
      const amtVal = parseFloat(form.amount) || 0;
      const dedPctVal = form.type === "Expense" ? (parseFloat(form.deductible_pct) ?? 100) : 100;
      const dedAmtVal = form.deductible_amount !== "" && form.deductible_amount != null
        ? parseFloat(form.deductible_amount)
        : (form.type === "Expense" ? Number((amtVal * (dedPctVal / 100)).toFixed(2)) : 0);

      const payload = overridePayload || {
        ...form,
        amount: amtVal,
        property_id: form.property_id || null,
        vendor_id: form.type === "Expense" ? (form.vendor_id || null) : null,
        tenant_id: form.type === "Income" ? (form.tenant_id || null) : null,
        receipt_items: isItemized ? JSON.stringify(aiItems) : form.receipt_items || null,
        deductible_pct: dedPctVal,
        deductible_amount: dedAmtVal,
        principal_amount: parseFloat(form.principal_amount) || 0,
        interest_amount: parseFloat(form.interest_amount) || 0,
        escrow_amount: parseFloat(form.escrow_amount) || 0,
        deductibility_reason: form.deductibility_reason || null,
        needs_cpa_review: Boolean(form.needs_cpa_review),
        cpa_review_reason: form.cpa_review_reason || null,
        is_capital_improvement: Boolean(form.is_capital_improvement),
        cpa_resolved: Boolean(form.cpa_resolved),
        cpa_notes: form.cpa_notes || null,
      };
      await api.post("/transactions", payload);
      toast.success("Transaction recorded");
      setShowForm(false);
      setShowMissingReceiptModal(false);
      setForm(EMPTY);
      setScanWithAi(false);
      setAiItems([]);
      setIsItemized(false);
      setAttachmentFile(null);
      if (receiptPreviewUrl) {
        try { URL.revokeObjectURL(receiptPreviewUrl); } catch {}
        setReceiptPreviewUrl(null);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save transaction");
    }
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    const amt = parseFloat(form.amount) || 0;
    const threshold = taxParameters?.receipt_warning_threshold ?? 75;

    // Check missing receipt threshold for expenses
    if (form.type === "Expense" && amt > threshold && !form.receipt_path) {
      setShowMissingReceiptModal(true);
      return;
    }

    await executeSave();
  };

  const handleUploadReceiptFromWarning = () => {
    setShowMissingReceiptModal(false);
    if (scanWithAi) {
      aiFileInputRef.current?.click();
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleSaveAnywayFromWarning = () => {
    executeSave();
  };

  const handleFlagCpaAndSaveFromWarning = () => {
    const amtVal = parseFloat(form.amount) || 0;
    const dedPctVal = parseFloat(form.deductible_pct) ?? 100;
    const dedAmtVal = form.deductible_amount !== "" && form.deductible_amount != null
      ? parseFloat(form.deductible_amount)
      : Number((amtVal * (dedPctVal / 100)).toFixed(2));

    const payload = {
      ...form,
      amount: amtVal,
      property_id: form.property_id || null,
      vendor_id: form.type === "Expense" ? (form.vendor_id || null) : null,
      tenant_id: form.type === "Income" ? (form.tenant_id || null) : null,
      receipt_items: isItemized ? JSON.stringify(aiItems) : form.receipt_items || null,
      deductible_pct: dedPctVal,
      deductible_amount: dedAmtVal,
      principal_amount: parseFloat(form.principal_amount) || 0,
      interest_amount: parseFloat(form.interest_amount) || 0,
      escrow_amount: parseFloat(form.escrow_amount) || 0,
      deductibility_reason: form.deductibility_reason || null,
      needs_cpa_review: true,
      cpa_review_reason: `Missing receipt for expense over $${taxParameters?.receipt_warning_threshold ?? 75} (IRC § 274(d))`,
      is_capital_improvement: Boolean(form.is_capital_improvement),
      cpa_resolved: false,
      cpa_notes: null,
    };
    executeSave(payload);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this transaction?")) return;
    await api.delete(`/transactions/${id}`);
    toast.success("Transaction removed");
    load();
  };

  const txList = Array.isArray(txs) ? txs.filter(Boolean) : [];

  // Available categories that actually have at least 1 transaction in txList
  const availableCategories = useMemo(() => {
    const counts = {};
    txList.forEach((t) => {
      const cat = t?.category || "Other";
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return Object.keys(counts)
      .sort()
      .map((name) => ({ name, count: counts[name] }));
  }, [txList]);

  // Combined real-time filtering: search query + category chips + type toggle
  const filtered = useMemo(() => {
    return txList.filter((tx) => {
      // 1. All/Income/Expense toggle
      if (filter !== "All" && tx?.type !== filter) return false;

      // 2. Category multi-selection filter
      if (selectedCategories.length > 0 && !selectedCategories.includes(tx?.category || "Other")) {
        return false;
      }

      // 3. Search query filter (matches category, vendor, tenant, property, notes, amount)
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const category = (tx?.category || "").toLowerCase();
        const vendor = (tx?.vendor_name || "").toLowerCase();
        const tenant = (tx?.tenant_name || "").toLowerCase();
        const property = (tx?.property_name || "").toLowerCase();
        const notes = (tx?.notes || "").toLowerCase();
        const amountStr = String(tx?.amount || "").replace(/[^0-9.]/g, "");
        const qDigits = q.replace(/[^0-9.]/g, "");

        const matchesText =
          category.includes(q) ||
          vendor.includes(q) ||
          tenant.includes(q) ||
          property.includes(q) ||
          notes.includes(q);

        const matchesAmount = qDigits.length > 0 && amountStr.includes(qDigits);

        if (!matchesText && !matchesAmount) return false;
      }

      return true;
    });
  }, [txList, filter, selectedCategories, searchQuery]);

  const toggleCategoryFilter = (catName) => {
    if (catName === "all") {
      setSelectedCategories([]);
      return;
    }
    setSelectedCategories((prev) =>
      prev.includes(catName) ? prev.filter((c) => c !== catName) : [...prev, catName]
    );
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setSelectedCategories([]);
    setFilter("All");
  };

  const hasActiveFilters = searchQuery.trim() !== "" || selectedCategories.length > 0 || filter !== "All";

  const catOptions = form.type === "Income" ? CATEGORIES_INCOME : CATEGORIES_EXPENSE;

  const totalExpenses = txList
    .filter((t) => t?.type === "Expense")
    .reduce((s, t) => s + (parseFloat(t?.amount) || 0), 0);

  const totalDeductible = txList
    .filter((t) => t?.type === "Expense" && !t?.is_capital_improvement)
    .reduce((s, t) => {
      if (t?.deductible_amount != null && t?.deductible_amount !== "") {
        return s + (parseFloat(t.deductible_amount) || 0);
      }
      const pct = t?.deductible_pct != null ? parseFloat(t.deductible_pct) : 100;
      return s + (parseFloat(t?.amount) || 0) * (pct / 100);
    }, 0);

  const totalNonDeductibleOrCap = txList
    .filter((t) => t?.type === "Expense")
    .reduce((s, t) => {
      if (t?.is_capital_improvement) return s + (parseFloat(t?.amount) || 0);
      if (t?.deductible_amount != null && t?.deductible_amount !== "") {
        const amt = parseFloat(t?.amount) || 0;
        const ded = parseFloat(t.deductible_amount) || 0;
        return s + Math.max(0, amt - ded);
      }
      const pct = t?.deductible_pct != null ? parseFloat(t.deductible_pct) : 100;
      return s + (parseFloat(t?.amount) || 0) * ((100 - pct) / 100);
    }, 0);

  const cpaReviewCount = txList.filter((t) => t?.needs_cpa_review && !t?.cpa_resolved).length;

  return (
    <Layout
      title="Books & Transactions"
      subtitle={`${txList.length} ${txList.length === 1 ? "entry" : "entries"} · IRS Schedule E Compliant`}
      hideNav={showForm || !!selectedTx || showMissingReceiptModal}
      actions={
        <div className="flex items-center gap-2">
          {can?.edit && (
            <button
              onClick={() => {
                setShowForm(true);
                setForm(EMPTY);
                setScanWithAi(false);
              }}
              data-testid="new-transaction-button"
              className="flex btn-primary text-xs sm:text-sm py-1.5 sm:py-2 px-2.5 sm:px-4 items-center gap-1.5 shrink-0 whitespace-nowrap"
            >
              <Plus size={15} /> New Transaction
            </button>
          )}
        </div>
      }
    >
      {/* New Transaction Form */}
      {showForm && (
        <div
          ref={formRef}
          className="card p-4 sm:p-6 mb-6 shadow-xl border border-[var(--border)] rounded-2xl animate-in fade-in slide-in-from-top-2 duration-200"
          data-testid="transaction-form"
        >
          <div className="flex items-center justify-between border-b border-[var(--border)] pb-4 mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold">
                {isBulkMode ? "New Transactions (Bulk Upload Mode)" : "New Transaction"}
              </h3>
              {isBulkMode && (
                <span className="tag bg-indigo-500/15 text-indigo-400 border border-indigo-500/30 text-[10px] font-bold">
                  Bulk Mode
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setForm(EMPTY);
                setAttachmentFile(null);
                if (receiptPreviewUrl) {
                  try { URL.revokeObjectURL(receiptPreviewUrl); } catch {}
                  setReceiptPreviewUrl(null);
                }
                setScanWithAi(false);
                setIsBulkMode(false);
                setBulkReceipts([]);
                resetAiScanProgress();
              }}
              className="text-xs text-[var(--muted)] font-semibold hover:text-[var(--text)]"
            >
              Cancel
            </button>
          </div>

          <form onSubmit={isBulkMode ? submitBulkTransactions : submit} className="space-y-4">
            {/* Top row: Type selection & Bulk Mode Toggle */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SelectField
                label="Type"
                value={form.type}
                onChange={(v) => {
                  setForm({
                    ...form,
                    type: v,
                    category: v === "Income" ? "Rent" : "Repairs",
                  });
                  if (v === "Income") {
                    setScanWithAi(false);
                    setIsBulkMode(false);
                  }
                }}
                options={["Expense", "Income"]}
                testId="tx-type"
                helpTopic={form.type === "Income" ? "income-types" : "schedule-e"}
              />

              {/* Bulk Mode Toggle (Positioned opposite Type as marked in screenshot) */}
              {form.type === "Expense" && (
                <div className="flex flex-col justify-end">
                  <div
                    onClick={() => setIsBulkMode(!isBulkMode)}
                    className={`p-2.5 sm:p-3 rounded-xl border transition-all cursor-pointer flex items-center justify-between gap-3 ${
                      isBulkMode
                        ? "bg-indigo-500/10 border-indigo-500/40 text-indigo-400 shadow-sm"
                        : "bg-[var(--surface-hover)] border-[var(--border)] text-[var(--muted)] hover:text-[var(--text)] hover:border-[var(--border-hover)]"
                    }`}
                    data-testid="toggle-bulk-mode-top"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                          isBulkMode
                            ? "bg-indigo-500/20 text-indigo-400"
                            : "bg-[var(--surface)] text-[var(--muted)] border border-[var(--border)]"
                        }`}
                      >
                        <Layers size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs font-bold text-[var(--text)] flex items-center gap-1.5">
                          <span>Bulk Receipt Mode</span>
                          {isBulkMode && (
                            <span className="text-[10px] bg-indigo-500/25 text-indigo-300 font-bold px-1.5 py-0.2 rounded font-mono">
                              ON
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-[var(--muted)] truncate">
                          Upload multiple receipts at once
                        </div>
                      </div>
                    </div>

                    <label
                      className="relative inline-flex items-center cursor-pointer shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={isBulkMode}
                        onChange={(e) => setIsBulkMode(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-10 h-5.5 bg-[var(--border)] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4.5 after:w-4.5 after:transition-all peer-checked:bg-indigo-500"></div>
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* AI OCR Toggle Option (Only for Expense) */}
            {form.type === "Expense" && (
              <div className="p-4 rounded-2xl bg-[var(--surface-hover)] border border-[var(--border)] flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1C3A5E] to-[#0D9488] text-white flex items-center justify-center shadow-sm shrink-0">
                    <Sparkles size={17} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-[var(--text)] flex items-center gap-1.5">
                      <span>{isBulkMode ? "Scan Receipts using AI (Gemini OCR)" : "Scan Receipt using AI (Gemini OCR)"}</span>
                      <HelpTip topic="receipt-scanner" />
                    </div>
                    <div className="text-xs text-[var(--muted)] truncate">
                      {isBulkMode
                        ? "Automatically extract amounts, dates, categories & vendors for each uploaded receipt"
                        : "Auto-extract amount, date, category & vendor from receipt or invoice"}
                    </div>
                  </div>
                </div>

                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    checked={scanWithAi}
                    onChange={(e) => {
                      setScanWithAi(e.target.checked);
                      if (!e.target.checked) {
                        setAiItems([]);
                        setIsItemized(false);
                      }
                    }}
                    className="sr-only peer"
                    data-testid="toggle-scan-ai"
                  />
                  <div className="w-11 h-6 bg-[var(--border)] peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
                </label>
              </div>
            )}

          {isBulkMode ? (
            <div className="space-y-5">
              {/* Bulk Mode Upload Dropzone */}
              <div className="p-6 border-2 border-dashed border-indigo-500/40 rounded-2xl bg-indigo-500/5 text-center relative overflow-hidden transition-all duration-300">
                {scanningAi ? (
                  <DocumentAnalysisProgress
                    progress={aiScanProgress}
                    status={aiScanStatus}
                    stepText={aiScanStepText}
                    docName={aiScanDocName}
                  />
                ) : uploading ? (
                  <div className="py-6 flex flex-col items-center justify-center gap-2">
                    <Loader2 size={28} className="animate-spin text-indigo-400" />
                    <div className="text-sm font-bold text-[var(--text)]">Uploading receipts to audit storage...</div>
                  </div>
                ) : (
                  <div>
                    <div className="w-12 h-12 rounded-2xl bg-indigo-500/15 text-indigo-400 flex items-center justify-center mx-auto mb-3 shadow-sm">
                      <Files size={24} />
                    </div>
                    <div className="text-sm font-bold text-[var(--text)] mb-1">
                      {bulkReceipts.length > 0
                        ? `${bulkReceipts.length} Receipt${bulkReceipts.length > 1 ? "s" : ""} Loaded in Batch`
                        : "Upload Multiple Receipts for Batch Categorization"}
                    </div>
                    <p className="text-xs text-[var(--muted)] mb-4 max-w-md mx-auto">
                      {scanWithAi
                        ? "Select or drop multiple receipt images (JPG, PNG, WEBP) or PDF statements. Gemini AI will automatically extract charges, vendors, dates, and Schedule E categories for each one."
                        : "Select or drop multiple receipt images (JPG, PNG) or PDF invoices. Each receipt will get its own section below for manual categorization."}
                    </p>
                    <div className="flex items-center justify-center gap-3">
                      <button
                        type="button"
                        onClick={() => bulkFileInputRef.current?.click()}
                        className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5 shadow-sm"
                        data-testid="choose-bulk-files-btn"
                      >
                        <Upload size={14} />
                        <span>{bulkReceipts.length > 0 ? "Add More Receipts" : "Choose Multiple Receipt Files"}</span>
                      </button>
                      {bulkReceipts.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setBulkReceipts([])}
                          className="btn-secondary text-xs py-2 px-3 text-red-400 hover:text-red-300 flex items-center gap-1"
                        >
                          <Trash2 size={13} />
                          <span>Clear Batch</span>
                        </button>
                      )}
                    </div>
                    <input
                      type="file"
                      ref={bulkFileInputRef}
                      onChange={handleBulkFileSelect}
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      multiple
                      className="hidden"
                      data-testid="bulk-file-input"
                    />
                  </div>
                )}
              </div>

              {/* Categorization Sections: Each Receipt Gets Its Own Card */}
              {bulkReceipts.length > 0 ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[var(--muted)] flex items-center gap-2">
                      <Layers size={14} className="text-indigo-400" />
                      <span>Receipts for Categorization ({bulkReceipts.length})</span>
                    </h4>
                    <button
                      type="button"
                      onClick={() => bulkFileInputRef.current?.click()}
                      className="text-xs text-indigo-500 hover:text-indigo-400 font-semibold flex items-center gap-1"
                    >
                      <Plus size={13} /> Add More Files
                    </button>
                  </div>

                  <div className="space-y-4">
                    {bulkReceipts.map((rcpt, idx) => (
                      <BulkReceiptCard
                        key={rcpt.id || idx}
                        receipt={rcpt}
                        index={idx}
                        properties={properties}
                        vendors={vendors}
                        categories={CATEGORIES_EXPENSE}
                        onChange={(updated) => {
                          const updatedList = [...bulkReceipts];
                          updatedList[idx] = updated;
                          setBulkReceipts(updatedList);
                        }}
                        onRemove={() => {
                          setBulkReceipts(bulkReceipts.filter((_, i) => i !== idx));
                        }}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="p-8 rounded-2xl border border-dashed border-[var(--border)] text-center text-xs text-[var(--muted)] space-y-2">
                  <Files size={32} className="mx-auto text-[var(--muted)]/50" />
                  <p className="font-semibold text-sm text-[var(--text)]">No receipts in batch yet</p>
                  <p>Click "Choose Multiple Receipt Files" above to upload and automatically categorize your receipts.</p>
                </div>
              )}

              {/* Bulk Mode Summary & Save Bar */}
              <div className="p-4 rounded-2xl bg-[var(--surface-hover)] border border-[var(--border)] flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4 text-xs flex-wrap">
                  <div>
                    <span className="text-[var(--muted)]">Total Receipts: </span>
                    <strong className="text-[var(--text)]">{bulkReceipts.length}</strong>
                  </div>
                  <div>
                    <span className="text-[var(--muted)]">Combined Amount: </span>
                    <strong className="text-slate-900 dark:text-slate-100 font-bold">
                      ${bulkReceipts.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0).toFixed(2)}
                    </strong>
                  </div>
                  <div>
                    <span className="text-[var(--muted)]">Deductible: </span>
                    <strong className="text-emerald-700 dark:text-emerald-400 font-bold">
                      ${bulkReceipts.reduce((sum, r) => {
                        const amt = parseFloat(r.amount) || 0;
                        const pct = parseFloat(r.deductible_pct) ?? 100;
                        return sum + (r.deductible_amount != null && r.deductible_amount !== "" ? parseFloat(r.deductible_amount) : (amt * (pct / 100)));
                      }, 0).toFixed(2)}
                    </strong>
                  </div>
                </div>

                <div className="flex items-center gap-2 self-end sm:self-auto">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setBulkReceipts([]);
                      setIsBulkMode(false);
                    }}
                    className="btn-secondary text-xs py-2 px-3"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={submitBulkTransactions}
                    disabled={savingBulk || bulkReceipts.length === 0 || scanningAi || uploading}
                    className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5 shadow-sm"
                    data-testid="bulk-submit-btn"
                  >
                    {savingBulk ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        <span>Saving {bulkReceipts.length} Transactions…</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={14} />
                        <span>Save All ({bulkReceipts.length}) Transactions</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* AI Scan Dropzone (Visible when toggled ON) */}
              {form.type === "Expense" && scanWithAi && (
                <div className="p-5 border-2 border-dashed border-teal-500/40 rounded-2xl bg-teal-500/5 text-center relative overflow-hidden transition-all duration-300">
                  {scanningAi ? (
                    <DocumentAnalysisProgress
                      progress={aiScanProgress}
                      status={aiScanStatus}
                      stepText={aiScanStepText}
                      docName={aiScanDocName}
                    />
                  ) : (
                    <div>
                      <Sparkles size={28} className="text-teal-400 mx-auto mb-2" />
                      <div className="text-sm font-bold mb-1">
                        {form.receipt_path || attachmentFile
                          ? `Receipt Scanned & Attached: ${attachmentFile?.name || (form.receipt_path ? form.receipt_path.split("/").pop() : "Receipt Attached")}`
                          : "Upload Receipt for AI Extraction"}
                      </div>
                      <p className="text-xs text-[var(--muted)] mb-3">
                        {form.receipt_path || attachmentFile
                          ? "Transaction fields populated. Receipt is saved below as your IRS audit record."
                          : "Select receipt photo (JPG, PNG, WEBP) or PDF invoice to automatically fill fields & attach for audit"}
                      </p>
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => aiFileInputRef.current?.click()}
                          className="btn-primary text-xs py-2 px-4 flex items-center gap-1.5"
                        >
                          <Upload size={14} />
                          <span>{form.receipt_path || attachmentFile ? "Replace Receipt" : "Choose Receipt File"}</span>
                        </button>
                        {attachmentFile && !aiItems.length && (
                          <button
                            type="button"
                            onClick={handleScanExistingAttachment}
                            className="btn-secondary text-xs py-2 px-3 text-teal-400 hover:text-teal-300 flex items-center gap-1.5"
                          >
                            <Sparkles size={13} />
                            <span>Scan Attached File</span>
                          </button>
                        )}
                        {(form.receipt_path || attachmentFile) && (
                          <button
                            type="button"
                            onClick={handleRemoveReceipt}
                            className="btn-secondary text-xs py-2 px-3 text-red-400 hover:text-red-300 flex items-center gap-1"
                          >
                            <Trash2 size={13} />
                            <span>Remove</span>
                          </button>
                        )}
                      </div>
                      <input
                        type="file"
                        ref={aiFileInputRef}
                        onChange={handleAiScanFile}
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        className="hidden"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Itemize Line Items Box (Visible if items extracted) */}
              {form.type === "Expense" && scanWithAi && aiItems.length > 0 && (
                <div className="card p-4 border-teal-500/30 bg-[var(--surface-hover)]/60">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <ListChecks size={16} className="text-teal-400" />
                      <span className="text-xs font-bold uppercase tracking-wider text-[var(--text)]">
                        Itemized Receipt Lines ({aiItems.length})
                      </span>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-[var(--text)]">
                      <input
                        type="checkbox"
                        checked={isItemized}
                        onChange={(e) => toggleItemize(e.target.checked)}
                        className="rounded border-[var(--border)] text-teal-500 focus:ring-teal-500"
                        data-testid="checkbox-toggle-itemize"
                      />
                      <span>Itemize & Exclude Personal Items</span>
                    </label>
                  </div>

                  {isItemized ? (
                    <div className="space-y-1.5 pt-1">
                      <p className="text-[11px] text-[var(--muted)] mb-2">
                        Uncheck personal or non-deductible items to automatically exclude them from the transaction amount:
                      </p>
                      <div className="divide-y divide-[var(--border)] border border-[var(--border)] rounded-xl overflow-hidden bg-[var(--surface)]">
                        {aiItems.map((item, idx) => (
                          <label
                            key={item.id}
                            className="flex items-center justify-between p-2.5 px-3 hover:bg-[var(--surface-hover)] cursor-pointer text-xs transition-colors"
                          >
                            <div className="flex items-center gap-2.5 flex-1 min-w-0 pr-2">
                              <input
                                type="checkbox"
                                checked={item.selected}
                                onChange={(e) => toggleItemCheck(idx, e.target.checked)}
                                className="rounded border-[var(--border)] text-teal-500 focus:ring-teal-500 shrink-0"
                              />
                              <span className={`truncate ${!item.selected ? "line-through text-[var(--muted)]" : "text-[var(--text)]"}`}>
                                {item.description}
                              </span>
                            </div>
                            <span className={`font-mono font-bold ${!item.selected ? "line-through text-[var(--muted)]" : "text-[var(--text)]"}`}>
                              ${parseFloat(item.amount || 0).toFixed(2)}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-[11px] text-[var(--muted)] flex items-center justify-between pt-1">
                      <span>{aiItems.length} lines extracted from receipt. Click "Itemize & Exclude" to select individual deductible lines.</span>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Date"
                  type="date"
                  value={form.date}
                  onChange={(v) => setForm({ ...form, date: v })}
                  required
                  testId="tx-date"
                />
                <Field
                  label="Amount ($)"
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(v) => {
                    const amt = v;
                    const pct = form.type === "Expense" ? (form.deductible_pct ?? 100) : 100;
                    const dedAmt = form.type === "Expense"
                      ? Number(((parseFloat(amt) || 0) * (pct / 100)).toFixed(2))
                      : Number((parseFloat(amt) || 0).toFixed(2));
                    setForm({ ...form, amount: amt, deductible_amount: dedAmt });
                  }}
                  required
                  placeholder="0.00"
                  testId="tx-amount"
                />
                <div>
                  <SelectField
                    label="Category"
                    value={form.category}
                    onChange={(v) => {
                      const isMortgage = v === "Mortgage Interest";
                      const amt = parseFloat(form.amount) || 0;
                      setForm({
                        ...form,
                        category: v,
                        interest_amount: isMortgage ? (form.interest_amount || amt) : form.interest_amount,
                        deductible_pct: isMortgage ? 100 : form.deductible_pct,
                        deductible_amount: isMortgage ? (form.interest_amount || amt) : form.deductible_amount,
                      });
                    }}
                    options={catOptions}
                    testId="tx-category"
                    helpTopic={CATEGORY_TOPIC_MAP[form.category] || "schedule-e"}
                  />
                  {form.type === "Expense" && (
                    <div className="mt-1">
                      <CategoryGuidanceCard category={form.category} amount={form.amount} />
                    </div>
                  )}
                </div>

                <div>
                  <label className="label">Property</label>
                  <select
                    className="lapen-input"
                    value={form.property_id || ""}
                    onChange={(e) => setForm((prev) => ({ ...prev, property_id: e.target.value }))}
                    data-testid="tx-property"
                  >
                    <option value="">— None (Portfolio-wide) —</option>
                    {(properties || []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}{p.address ? ` (${p.address})` : ""}
                      </option>
                    ))}
                  </select>
                </div>

                {form.type === "Income" && (
                  <div>
                    <label className="label">Tenant</label>
                    <select
                      className="lapen-input"
                      value={form.tenant_id || ""}
                      onChange={(e) => setForm((prev) => ({ ...prev, tenant_id: e.target.value }))}
                      data-testid="tx-tenant"
                    >
                      <option value="">— None —</option>
                      {(tenants || []).map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name} (Unit {t.unit})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {form.type === "Expense" && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="label mb-0">Vendor / Merchant</label>
                      <button
                        type="button"
                        onClick={() => {
                          setShowQuickAddVendor(true);
                          setNewVendorName(detectedVendor || "");
                        }}
                        className="text-[11px] text-teal-400 hover:text-teal-300 font-medium flex items-center gap-1"
                      >
                        <Plus size={12} /> Add New
                      </button>
                    </div>
                    <select
                      className="lapen-input"
                      value={form.vendor_id || ""}
                      onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
                      data-testid="tx-vendor"
                    >
                      <option value="">— None / Select Vendor —</option>
                      {vendors.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name} {v.default_category ? `(${v.default_category})` : ""}
                        </option>
                      ))}
                    </select>

                    {/* Quick Add Vendor Modal */}
                    {showQuickAddVendor && (
                      <div className="mt-2 p-3 rounded-xl bg-[var(--surface-hover)] border border-teal-500/30 flex items-center gap-2 text-xs">
                        <input
                          type="text"
                          className="lapen-input text-xs flex-1"
                          placeholder="Vendor Name"
                          value={newVendorName}
                          onChange={(e) => setNewVendorName(e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => handleQuickAddVendor(newVendorName)}
                          disabled={creatingVendor || !newVendorName.trim()}
                          className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1 shrink-0"
                        >
                          {creatingVendor ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                          <span>Save Vendor</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowQuickAddVendor(false)}
                          className="btn-secondary text-xs py-1.5 px-2.5"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Smart Clarifying Prompt for IRS rules */}
                {form.type === "Expense" && taxParameters?.show_smart_deductibility_prompts && (
                  <div className="sm:col-span-2">
                    <SmartClarifyingPrompt
                      category={form.category}
                      amount={parseFloat(form.amount) || 0}
                      taxParameters={taxParameters || {}}
                      value={form}
                      onChange={(fields) => {
                        setForm((prev) => {
                          const updated = { ...prev, ...fields };
                          if (fields?.deductible_pct != null && updated.amount) {
                            const amt = parseFloat(updated.amount) || 0;
                            updated.deductible_amount = Number((amt * (fields.deductible_pct / 100)).toFixed(2));
                          }
                          return updated;
                        });
                      }}
                      onOpenImprovementModal={() => navigate("/improvements?new=1")}
                    />
                  </div>
                )}

                {/* Mortgage Breakdown Card */}
                {form.type === "Expense" && form.category === "Mortgage Interest" && (
                  <div className="sm:col-span-2">
                    <MortgageBreakdownCard
                      amount={form.amount}
                      principalAmount={form.principal_amount}
                      interestAmount={form.interest_amount}
                      escrowAmount={form.escrow_amount}
                      deductibleAmount={form.deductible_amount}
                      deductiblePct={form.deductible_pct}
                      onChange={handleMortgageBreakdownChange}
                      readOnly={false}
                    />
                  </div>
                )}

                {/* Tax Deductibility & CPA Flagging Controls */}
                {form.type === "Expense" && (
                  <div className="sm:col-span-2 p-4 rounded-2xl bg-[var(--surface-hover)] border border-[var(--border)] space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-bold text-[var(--text)] flex items-center gap-2">
                        <ShieldCheck size={16} className="text-emerald-400" />
                        <span>Schedule E Tax Deductibility Settings</span>
                      </div>
                      <DeductibilityStatusPill
                        deductiblePct={form.deductible_pct}
                        isCapitalImprovement={form.is_capital_improvement}
                        needsCpaReview={form.needs_cpa_review}
                        cpaResolved={form.cpa_resolved}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                      <div>
                        <label className="text-xs font-semibold text-[var(--muted)] block mb-1">
                          Deductible Percentage (%)
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="any"
                            className="lapen-input text-xs"
                            value={form.deductible_pct ?? 100}
                            onChange={(e) => {
                              const pct = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                              const amt = parseFloat(form.amount) || 0;
                              const dedAmt = Number((amt * (pct / 100)).toFixed(2));
                              setForm({
                                ...form,
                                deductible_pct: pct,
                                deductible_amount: dedAmt,
                              });
                            }}
                          />
                          <span className="text-xs text-[var(--muted)] font-bold">%</span>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-[var(--muted)] block mb-1">
                          Calculated Deductible Amount ($)
                        </label>
                        <div className="relative flex items-center">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 pointer-events-none select-none z-10">$</span>
                          <input
                            type="number"
                            step="any"
                            className="lapen-input text-xs !pl-8"
                            style={{ paddingLeft: "2rem" }}
                            value={form.deductible_amount ?? ""}
                            onChange={(e) => {
                              const val = e.target.value === "" ? "" : parseFloat(e.target.value) || 0;
                              const amt = parseFloat(form.amount) || 0;
                              const pct = amt > 0 && typeof val === "number" ? Math.min(100, Math.round((val / amt) * 100)) : form.deductible_pct;
                              setForm({
                                ...form,
                                deductible_amount: val,
                                deductible_pct: pct,
                              });
                            }}
                          />
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="text-xs font-semibold text-[var(--muted)] block mb-1">
                          Deductibility Basis / Reason
                        </label>
                        <input
                          type="text"
                          className="lapen-input text-xs"
                          value={form.deductibility_reason || ""}
                          onChange={(e) => setForm({ ...form, deductibility_reason: e.target.value })}
                          placeholder="e.g. 50% business meal, 100% routine repair"
                        />
                      </div>
                    </div>

                    <div className="space-y-2 pt-2 border-t border-[var(--border)]">
                      <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-[var(--text)]">
                        <input
                          type="checkbox"
                          checked={form.is_capital_improvement}
                          onChange={(e) => {
                            const isCap = e.target.checked;
                            setForm({
                              ...form,
                              is_capital_improvement: isCap,
                              deductible_pct: isCap ? 0 : 100,
                              deductible_amount: isCap ? 0 : form.amount,
                              deductibility_reason: isCap
                                ? "Capital Improvement (Depreciated over 27.5 yrs via Form 4562)"
                                : form.deductibility_reason,
                            });
                          }}
                          className="rounded border-[var(--border)] text-indigo-600 focus:ring-indigo-500"
                        />
                        <Building size={14} className="text-indigo-400" />
                        <span>Capital Improvement (Must be depreciated over 27.5 yrs on Form 4562)</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-[var(--text)]">
                        <input
                          type="checkbox"
                          checked={form.needs_cpa_review}
                          onChange={(e) => setForm({ ...form, needs_cpa_review: e.target.checked })}
                          className="rounded border-[var(--border)] text-amber-500 focus:ring-amber-500"
                        />
                        <Flag size={14} className="text-amber-400" />
                        <span>Flag this transaction for CPA Review</span>
                      </label>

                      {form.needs_cpa_review && (
                        <div className="space-y-2 pl-6 pt-1">
                          <input
                            type="text"
                            className="lapen-input text-xs"
                            value={form.cpa_review_reason || ""}
                            onChange={(e) => setForm({ ...form, cpa_review_reason: e.target.value })}
                            placeholder="Reason for CPA review (e.g. repair vs capitalization determination)"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}

              {/* Capital Improvement Form 4562 Guidance Banner */}
              {form.type === "Expense" && form.is_capital_improvement && (
                <div className="sm:col-span-2 p-3.5 rounded-xl bg-indigo-500/10 border border-indigo-500/30 text-xs text-indigo-300 space-y-1">
                  <div className="font-bold flex items-center gap-1.5 text-indigo-400">
                    <Building size={14} />
                    <span>IRS Tangible Property Regulations: Capital Improvement</span>
                  </div>
                  <p className="text-[11px] text-[var(--text)]">
                    This cost is capitalized and depreciated over 27.5 years (MACRS GDS) rather than deducted currently.
                    A capital asset record will be suggested upon saving.
                  </p>
                  <CategoryGuidanceCard
                    category="Improvements (CapEx)"
                    amount={form.amount}
                    onOpenImprovementModal={() => navigate("/improvements?new=1")}
                  />
                </div>
              )}

              {/* Notes / Description */}
              <div className="sm:col-span-2">
                <Field
                  label="Notes / Description"
                  value={form.notes}
                  onChange={(v) => setForm({ ...form, notes: v })}
                  placeholder="e.g. Repairs completed by plumber"
                  testId="tx-notes"
                />
              </div>

              {/* Receipt / Invoice Attachment (Audit Record) - Shared with OCR */}
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="label mb-0">Receipt / Invoice Attachment (Audit Record)</label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setIsBulkMode(true)}
                      className="text-[11px] text-indigo-500 hover:text-indigo-400 font-semibold flex items-center gap-1 px-2 py-0.5 rounded-lg hover:bg-indigo-500/10 transition-colors"
                      title="Upload multiple receipts in bulk"
                    >
                      <Layers size={12} />
                      <span>Switch to Bulk Upload</span>
                    </button>
                    {form.receipt_path && (
                      <span className="tag tag-success text-[10px] font-bold">
                        ✓ Attached for IRS Audit
                      </span>
                    )}
                  </div>
                </div>

                {form.receipt_path ? (
                  <div
                    className="p-3.5 rounded-2xl bg-[var(--surface-hover)] border border-[var(--border)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 transition-all"
                    data-testid="attached-receipt-card"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {/* Thumbnail or PDF badge */}
                      {form.receipt_path.toLowerCase().endsWith(".pdf") || attachmentFile?.type === "application/pdf" ? (
                        <div className="w-12 h-12 rounded-xl bg-red-500/15 text-red-400 flex flex-col items-center justify-center border border-red-500/25 shrink-0">
                          <FileText size={20} />
                          <span className="text-[9px] font-extrabold uppercase mt-0.5">PDF</span>
                        </div>
                      ) : (
                        <a
                          href={getReceiptUrl(form.receipt_path)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="relative group shrink-0 block"
                          title="Click to view full receipt"
                        >
                          <img
                            src={receiptPreviewUrl || getReceiptUrl(form.receipt_path)}
                            alt="Receipt Preview"
                            className="w-12 h-12 object-cover rounded-xl border border-[var(--border)] shadow-sm bg-black/20 group-hover:opacity-80 transition-opacity"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity text-white">
                            <ExternalLink size={14} />
                          </div>
                        </a>
                      )}

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold truncate text-[var(--text)]">
                            {attachmentFile?.name || form.receipt_path.split("/").pop() || "Receipt Attached"}
                          </span>
                          {aiItems.length > 0 && (
                            <span className="tag bg-teal-500/20 text-teal-400 text-[10px] font-bold flex items-center gap-1">
                              <Sparkles size={10} />
                              Attached via OCR
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-[var(--muted)] mt-0.5">
                          ✓ This receipt is permanently preserved for IRS Schedule E tax audit compliance.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0 self-end sm:self-center">
                      <a
                        href={getReceiptUrl(form.receipt_path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary text-xs py-1.5 px-2.5 flex items-center gap-1.5"
                        title="Open receipt in new tab"
                      >
                        <ExternalLink size={13} />
                        <span>View</span>
                      </a>

                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="btn-secondary text-xs py-1.5 px-2.5 flex items-center gap-1.5"
                        title="Replace receipt file"
                      >
                        <Upload size={13} />
                        <span>Replace</span>
                      </button>

                      <button
                        type="button"
                        onClick={handleRemoveReceipt}
                        className="p-1.5 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                        title="Remove attached receipt"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="w-full flex items-center justify-center gap-2 p-3.5 rounded-xl border-2 border-dashed border-[var(--border)] hover:border-teal-500/40 text-[var(--text-secondary)] text-sm font-medium transition-colors bg-[var(--surface-hover)]/30"
                    >
                      {uploading ? (
                        <>
                          <Loader2 size={16} className="animate-spin text-teal-400" />
                          <span>{`Uploading attachment: ${uploadProgressText || "Processing…"}`}</span>
                        </>
                      ) : (
                        <>
                          <Paperclip size={16} />
                          <span>Attach Receipt or Invoice (JPG, PNG, PDF)</span>
                        </>
                      )}
                    </button>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      className="hidden"
                    />
                    <p className="text-[11px] text-[var(--muted)] mt-1">
                      Attachments are permanently preserved for IRS Schedule E tax audit compliance (up to 25MB).
                    </p>
                  </div>
                )}
              </div>
            </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-[var(--border)]">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setForm(EMPTY);
                    setAttachmentFile(null);
                    if (receiptPreviewUrl) {
                      try { URL.revokeObjectURL(receiptPreviewUrl); } catch {}
                      setReceiptPreviewUrl(null);
                    }
                    setScanWithAi(false);
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" data-testid="tx-submit" className="btn-primary">
                  Save Transaction
                </button>
              </div>
            </>
          )}
          </form>
        </div>
      )}

      {/* Running Tax Deductibility Banner */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="card p-3.5 flex items-center justify-between">
          <div>
            <div className="text-[11px] text-slate-700 dark:text-slate-300 font-semibold">Total Recorded Expenses</div>
            <div className="text-xl font-black text-[var(--text)] mt-0.5">
              {formatCurrency(totalExpenses)}
            </div>
          </div>
          <div className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
            <DollarSign className="w-5 h-5" />
          </div>
        </div>

        <div className="card p-3.5 flex items-center justify-between border-emerald-500/20 bg-emerald-50/30 dark:bg-emerald-950/10">
          <div>
            <div className="text-[11px] text-emerald-800 dark:text-emerald-400 font-semibold">Schedule E Deductible</div>
            <div className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5">
              {formatCurrency(totalDeductible)}
            </div>
          </div>
          <div className="p-2 rounded-xl bg-emerald-100 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400">
            <ShieldCheck className="w-5 h-5" />
          </div>
        </div>

        <div className="card p-3.5 flex items-center justify-between">
          <div>
            <div className="text-[11px] text-slate-700 dark:text-slate-300 font-semibold">Non-Deductible / Cap</div>
            <div className="text-xl font-black text-slate-900 dark:text-slate-100 mt-0.5">
              {formatCurrency(totalNonDeductibleOrCap)}
            </div>
          </div>
          <div className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500">
            <PieChart className="w-5 h-5" />
          </div>
        </div>

        <div
          onClick={() => navigate("/cpa-review")}
          className={`card p-3.5 flex items-center justify-between cursor-pointer transition-all hover:scale-[1.01] ${
            cpaReviewCount > 0
              ? "border-amber-500/30 bg-amber-50/40 dark:bg-amber-950/20 hover:border-amber-500/50"
              : "border-[var(--border)]"
          }`}
        >
          <div>
            <div className="text-[11px] text-amber-800 dark:text-amber-400 font-semibold flex items-center gap-1">
              <Flag className="w-3 h-3 fill-amber-500 text-amber-500" />
              <span>CPA Review Queue</span>
            </div>
            <div className="text-xl font-black text-amber-600 dark:text-amber-400 mt-0.5 flex items-center gap-1.5">
              <span>{cpaReviewCount} {cpaReviewCount === 1 ? "Item" : "Items"}</span>
              <ArrowRight className="w-4 h-4 opacity-70" />
            </div>
          </div>
          <div className="p-2 rounded-xl bg-amber-100 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400">
            <Flag className="w-5 h-5" />
          </div>
        </div>
      </div>

      {/* Search Bar (Fix 2.A) */}
      <div className="relative mb-3" data-testid="tx-search-container">
        <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by name, vendor, or amount..."
          className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-[var(--border)] bg-[var(--surface)] text-[var(--text)] text-sm placeholder:text-[var(--muted)] focus:outline-none focus:border-teal-500 transition-colors"
          data-testid="tx-search-input"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[var(--muted)] hover:text-[var(--text)]"
            title="Clear search"
            aria-label="Clear search"
          >
            <X size={15} />
          </button>
        )}
      </div>

      {/* Category Filter Chips (Fix 2.B) */}
      {availableCategories.length > 0 && (
        <div className="flex items-center gap-2 mb-3.5 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1" data-testid="tx-category-chips">
          <button
            type="button"
            onClick={() => toggleCategoryFilter("all")}
            className={`text-xs px-3 py-1.5 rounded-full font-semibold whitespace-nowrap shrink-0 transition-all ${
              selectedCategories.length === 0
                ? "bg-teal-600 text-white shadow-sm"
                : "bg-[var(--surface)] border border-[var(--border)] text-[var(--muted)] hover:text-[var(--text)] hover:border-[var(--border-hover)]"
            }`}
            data-testid="category-chip-all"
          >
            All Categories
          </button>
          {availableCategories.map((c) => {
            const isSelected = selectedCategories.includes(c.name);
            return (
              <button
                key={c.name}
                type="button"
                onClick={() => toggleCategoryFilter(c.name)}
                className={`text-xs px-3 py-1.5 rounded-full font-semibold whitespace-nowrap shrink-0 transition-all flex items-center gap-1.5 ${
                  isSelected
                    ? "bg-teal-600 text-white shadow-sm"
                    : "bg-[var(--surface)] border border-[var(--border)] text-[var(--muted)] hover:text-[var(--text)] hover:border-[var(--border-hover)]"
                }`}
                data-testid={`category-chip-${c.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
              >
                <span>{c.name}</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${isSelected ? "bg-white/20 text-white" : "bg-[var(--surface-hover)] text-[var(--muted)]"}`}>
                  {c.count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Filter Segmented Control */}
      <div className="segmented mb-3" data-testid="tx-filter">
        {["All", "Income", "Expense"].map((f) => (
          <button
            key={f}
            className={filter === f ? "active" : ""}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f.toLowerCase()}`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Combined Filter Result Count & Clear Filters Link (Fix 2.C) */}
      {hasActiveFilters && (
        <div className="flex items-center justify-between text-xs text-[var(--muted)] mb-3 px-1">
          <span>
            Showing <strong className="text-[var(--text)]">{filtered.length}</strong> of{" "}
            <strong className="text-[var(--text)]">{txList.length}</strong> transactions
          </span>
          <button
            type="button"
            onClick={clearAllFilters}
            className="text-teal-400 hover:underline font-semibold cursor-pointer"
            data-testid="clear-filters-btn"
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Pull to Refresh Indicator - Bug 8.C */}
      {(pullDistance > 0 || isRefreshing) && (
        <div
          className="flex items-center justify-center transition-all overflow-hidden mb-2"
          style={{ height: `${pullDistance}px` }}
        >
          <div className="flex items-center gap-2 text-xs font-semibold text-teal-400 py-2">
            <Loader2 size={16} className="animate-spin" />
            <span>{isRefreshing ? "Refreshing transactions…" : "Release to refresh"}</span>
          </div>
        </div>
      )}

      {/* Swipe to Delete Hint for Mobile - Bug 8.B */}
      {!hasSeenSwipeHint && filtered.length > 0 && (
        <div className="sm:hidden mb-3 px-3.5 py-2 rounded-xl bg-teal-500/10 border border-teal-500/25 flex items-center justify-between text-xs text-teal-400 animate-in fade-in duration-200">
          <span className="flex items-center gap-1.5 font-medium">
            <span>← Swipe left on any row to delete</span>
          </span>
          <button
            type="button"
            onClick={dismissSwipeHint}
            className="text-[var(--muted)] hover:text-white p-1"
            aria-label="Dismiss hint"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Transactions List with 140px bottom padding on mobile so content scrolls completely clear of FAB */}
      <div className="pb-[140px] sm:pb-8">
        {loading ? (
          <TransactionsSkeleton />
        ) : loadError ? (
          <DataErrorState message={loadError} onRetry={() => load()} />
        ) : hasActiveFilters && filtered.length === 0 ? (
          <div className="card p-8 text-center rounded-2xl border border-[var(--border)] my-4">
            <div className="w-12 h-12 rounded-xl bg-slate-800 text-slate-400 flex items-center justify-center mx-auto mb-3">
              <Search size={22} />
            </div>
            <h4 className="text-base font-bold text-[var(--text)] mb-1">No transactions match your search</h4>
            <p className="text-xs text-[var(--muted)] mb-4">
              Try adjusting your search query, selecting different categories, or clearing filters.
            </p>
            <button
              type="button"
              onClick={clearAllFilters}
              className="btn-secondary text-xs py-2 px-4 inline-flex items-center gap-1.5"
              data-testid="empty-search-clear-btn"
            >
              <X size={14} /> Clear all filters
            </button>
          </div>
        ) : (
          <div
            className="card divide-y divide-[var(--border)] overflow-hidden"
            data-testid="transactions-list"
            onTouchStart={handleTouchStartList}
            onTouchMove={handleTouchMoveList}
            onTouchEnd={handleTouchEndList}
          >
            {filtered.length === 0 && (
              <div className="p-4">
                <EmptyState
                  icon={Receipt}
                  title="No Transactions Recorded Yet"
                  description="Record rental income, repairs, mortgage interest, and expenses mapped to IRS Schedule E."
                  helpTopic="schedule-e"
                  helpText="Learn about Schedule E & Tax Deductions"
                  actionLabel="+ New Transaction"
                  onAction={() => {
                    setShowForm(true);
                    setForm(EMPTY);
                    setScanWithAi(false);
                  }}
                  testId="transactions-empty-state"
                />
              </div>
            )}

            {filtered.map((tx) => (
              <SwipeableTransactionRow
                key={tx.id}
                tx={tx}
                onSelect={setSelectedTx}
                onDelete={remove}
                canDelete={Boolean(can?.delete)}
                onSwipeAction={dismissSwipeHint}
              />
            ))}
          </div>
        )}
      </div>

      {/* Floating Action Button (FAB) for Mobile - Fix 5: bottom 80px, right 16px */}
      {can?.edit && !showForm && !selectedTx && !showMissingReceiptModal && (
        <button
          type="button"
          onClick={() => {
            setShowForm(true);
            setForm(EMPTY);
            setScanWithAi(false);
          }}
          data-testid="fab-new-transaction"
          aria-label="New Transaction"
          className="sm:hidden fixed bottom-[80px] right-4 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-[#1C3A5E] to-[#0D9488] text-white flex items-center justify-center shadow-2xl shadow-teal-500/30 hover:scale-105 active:scale-95 transition-all border border-teal-400/30 cursor-pointer"
        >
          <Plus size={24} className="text-white" />
        </button>
      )}

      {/* Transaction Detail & Receipt Viewer Modal */}
      {selectedTx && (
        <TransactionDetailModal
          transaction={selectedTx}
          onClose={() => setSelectedTx(null)}
          onDelete={remove}
          onUpdate={(updated) => {
            setTxs((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
            setSelectedTx(updated);
          }}
          canDelete={Boolean(can?.delete)}
          properties={properties}
          vendors={vendors}
          tenants={tenants}
        />
      )}

      {/* Missing Receipt Warning Modal */}
      <MissingReceiptModal
        isOpen={showMissingReceiptModal}
        amount={parseFloat(form.amount) || 0}
        threshold={taxParameters?.receipt_warning_threshold ?? 75}
        onClose={() => setShowMissingReceiptModal(false)}
        onUploadReceipt={handleUploadReceiptFromWarning}
        onSaveAnyway={handleSaveAnywayFromWarning}
        onFlagCpaAndSave={handleFlagCpaAndSaveFromWarning}
      />

    </Layout>
  );
}

